import type { IdentifyProperties } from '@/ampli';
import { ownedDmcIds } from '@/app/api/lib/auth/owned-dmcs';
import type { Session } from '@/app/api/lib/auth/types';

/**
 * Seuil repris de `apps/odys/src/app/Tracking.tsx` pour que `device_category`
 * ait la même signification dans les deux sources du même projet Amplitude.
 */
const MOBILE_MAX_WIDTH = 770;

/**
 * DTO d'identité passé au composant client. Union discriminée, comme `Session`,
 * pour rendre l'impossible inexprimable : si le rôle n'est pas `visitor`,
 * l'email existe. Un objet plat à champs optionnels obligerait à inventer un
 * `user_id` vide.
 *
 * Volontairement plus étroit que `Session` : ce qui est passé à un composant
 * client est sérialisé dans le HTML, donc l'exposition doit être explicite.
 */
export type TrackingViewer =
    | { role: 'visitor' }
    | {
          role: 'agency' | 'dmc';
          email: string;
          agencyName?: string;
          allowedDmcIds: readonly string[];
      };

/**
 * Un compte `pending` porte un email mais reste gaté en Visitor : on ne
 * l'identifie pas.
 */
export function toTrackingViewer(session: Session): TrackingViewer {
    if (session.role === 'visitor') {
        return { role: 'visitor' };
    }

    return {
        role: session.role,
        email: session.email,
        agencyName: session.agencyName,
        allowedDmcIds: ownedDmcIds(session),
    };
}

/**
 * Le tracking plan est partagé avec odys : il n'existe ni `viewer_role`, ni
 * `agency_name`, ni `locale`. Le rôle catalog va donc dans `user_roles` et le
 * nom d'agence dans `company` ; la locale n'est pas mesurée.
 */
export function buildIdentity(
    viewer: TrackingViewer,
    viewportWidth: number,
): { userId: string; properties: IdentifyProperties } | null {
    if (viewer.role === 'visitor') {
        return null;
    }

    return {
        userId: viewer.email,
        properties: {
            company: viewer.agencyName,
            device_category: viewportWidth < MOBILE_MAX_WIDTH ? 'mobile' : 'desktop',
            dmc_ids: [...viewer.allowedDmcIds],
            user_email: viewer.email,
            user_id: viewer.email,
            user_roles: [viewer.role],
        },
    };
}
